import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Select, { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import Table from "@amzn/awsui-components-react/polaris/table";
import Box from "@amzn/awsui-components-react/polaris/box";
import { API } from "aws-amplify";
import { ConsumptionComputationStrategy } from "../../../consts/ApprovalsAndOverrides";

interface Props {
  profileId: string;
  drugAsin: string;
}

interface ConsumptionEstimatesResponse {
  customerZeroQuantityDate: number;
  earliestOrderDate: number;
  nextOrderDate: number;
  nextOrderWithInsuranceDate: number | null;
}

interface State {
  computationStrategy: string;
  isLoading: boolean;
  response: ConsumptionEstimatesResponse | null;
  error: string | null;
}

const STRATEGY_OPTIONS: SelectProps.Option[] = [
  { label: "CACHE_FIRST — Return previously computed estimates if available, otherwise compute fresh.", value: ConsumptionComputationStrategy.CACHE_FIRST },
  { label: "RECOMPUTE — Always perform a fresh computation, ignoring any previously computed values.", value: ConsumptionComputationStrategy.RECOMPUTE },
];

class ConsumptionEstimatesSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      computationStrategy: ConsumptionComputationStrategy.CACHE_FIRST,
      isLoading: false,
      response: null,
      error: null,
    };
  }

  private handleSubmit = async () => {
    const { profileId, drugAsin } = this.props;
    const { computationStrategy } = this.state;

    this.setState({ isLoading: true, error: null, response: null });

    try {
      const response = await API.post("ApaqPortalAPI", "/consumption/estimates", {
        body: { profileId, drugAsin, computationStrategy },
      });
      this.setState({ response, isLoading: false });
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to fetch consumption estimates";
      this.setState({ error: message, isLoading: false });
    }
  };

  private formatDate = (epochMillis: number | null): string => {
    if (epochMillis == null) return "N/A";
    return new Date(epochMillis).toLocaleString();
  };

  render() {
    const { computationStrategy, isLoading, response, error } = this.state;
    const selectedOption = STRATEGY_OPTIONS.find(o => o.value === computationStrategy) || null;

    const tableItems = response ? [
      { field: "Customer Zero Quantity Date", date: this.formatDate(response.customerZeroQuantityDate) },
      { field: "Earliest Order Date", date: this.formatDate(response.earliestOrderDate) },
      { field: "Next Order Date", date: this.formatDate(response.nextOrderDate) },
      { field: "Next Order With Insurance Date", date: this.formatDate(response.nextOrderWithInsuranceDate) },
    ] : [];

    return (
      <Container header={
        <Header
          variant="h3"
          actions={
            <Button variant="primary" onClick={this.handleSubmit} loading={isLoading}>
              Get Estimates
            </Button>
          }
        >
          Consumption Date Estimates
        </Header>
      }>
        <SpaceBetween size="l">
          <FormField label="Computation Strategy">
            <Select
              selectedOption={selectedOption}
              onChange={({ detail }) => this.setState({ computationStrategy: detail.selectedOption.value || ConsumptionComputationStrategy.CACHE_FIRST })}
              options={STRATEGY_OPTIONS}
            />
          </FormField>

          {error && (
            <Box color="text-status-error">{error}</Box>
          )}

          {response && (
            <>
              <Box color="text-body-secondary" fontSize="body-s">
                Times displayed in your local timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </Box>
              <Table
                columnDefinitions={[
                  { id: "field", header: "Field", cell: (item: any) => item.field },
                  { id: "date", header: "Date", cell: (item: any) => item.date },
                ]}
                items={tableItems}
                variant="embedded"
              />
              <pre style={{
                backgroundColor: "#f5f5f5",
                border: "1px solid lightgray",
                borderRadius: "4px",
                padding: "10px",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "normal",
              }}>
                {JSON.stringify(response, null, 2)}
              </pre>
            </>
          )}
        </SpaceBetween>
      </Container>
    );
  }
}

export default ConsumptionEstimatesSection;
